import { useTranslation } from "react-i18next";
import { displayToast } from "../../components/app/AppToast";
import { isDesktopShell } from "../../common/hooks/useIsDesktopView";
import { createMemoryStore } from "../../lib/store";
import { apiClient, useApiRevalidate } from "../../services/api";
import { gaEventVisualAbstractRequested } from "../../tools/analytics/domains/article.analytics";
import {
  dismissFailedVisualAbstract,
  watchVisualAbstract,
} from "./visualAbstractWatchStore";

/**
 * The articles whose generation request is in flight on this tab. A Generate
 * button reads it to disable itself until the request answers. Memory only: a
 * request does not outlive the tab that sent it.
 */
const useRequestingVisualAbstracts = createMemoryStore<string[]>([]);

/**
 * The POST alone, null when it fails. Only this step can fail as a request the
 * reader should hear about: once it has answered 202 the run exists, and the
 * steps after it record the run.
 */
async function postVisualAbstractRequest(
  articleId: string,
  roomId: string | null
) {
  try {
    return await apiClient.post(
      "/webapp/private/articles/:id/visual-abstracts",
      { param: { id: articleId }, json: { roomId } }
    );
  } catch {
    // The api client's onRequestError already captured the cause; the 422 an
    // eligibility change can raise between the GET and this POST is a race
    // the reader cannot act on either.
    return null;
  }
}

/**
 * Asks for a poster from the visual abstracts library, the confirmation panel
 * of the creation screen or a failed card's retry, and hands the run to the
 * app-wide watcher.
 */
export function useRequestVisualAbstract(): {
  isRequesting: (articleId: string) => boolean;
  request: (input: {
    articleId: string;
    articleTitle: string;
    roomId: string | null;
  }) => Promise<"requested" | "failed">;
} {
  const { t } = useTranslation();
  const revalidate = useApiRevalidate();
  const [requesting, setRequesting] = useRequestingVisualAbstracts();

  async function request({
    articleId,
    articleTitle,
    roomId,
  }: {
    articleId: string;
    articleTitle: string;
    roomId: string | null;
  }): Promise<"requested" | "failed"> {
    // Held until the caller is handed the outcome, not just until the POST
    // answers: a second click during the refresh below would send a second
    // request and step back twice, leaving the library behind.
    const release = (): void => {
      setRequesting((previous) => previous.filter((id) => id !== articleId));
    };
    setRequesting((previous) => [...previous, articleId]);
    const response = await postVisualAbstractRequest(articleId, roomId);
    if (response === null) {
      release();
      displayToast(t("Unable to generate the visual abstract"), "error");
      return "failed";
    }
    // Registered from the 202: the library's generating card and the
    // completion toast both read the watch list.
    watchVisualAbstract({ id: response.body.id, articleId, articleTitle });
    // A new run of the pair replaces its failed card, if it had one.
    dismissFailedVisualAbstract({ articleId, roomId });
    gaEventVisualAbstractRequested({
      articleId,
      roomId,
      sourceType: response.body.sourceType,
      trigger: "library",
    });
    // The article's list is the key the pill, the chat card and the creation
    // panel read: refreshed before leaving, so reopening the paper shows the
    // run instead of offering a second one.
    await revalidate("/webapp/private/articles/:id/visual-abstracts", {
      param: { id: articleId },
      query: { roomId },
    }).finally(release);
    if (isDesktopShell())
      displayToast(t("Generating your visual abstract…"), "generating");
    else displayToast(t("Visual abstract generation started"), "success");
    return "requested";
  }

  return {
    isRequesting: (articleId) => requesting.includes(articleId),
    request,
  };
}
